<template>
  <a-menu
    v-if="showLoginedMenu"
    mode="inline"
    class="w-full md:h-full"
    :items="menuItems"
    v-model:selectedKeys="selectedKeys"
    v-model:openKeys="openKeys"
    @click="handleClick"
  />
</template>

<script lang="ts" setup>
import type { MenuProps } from "ant-design-vue";
import { FundOutlined, ShoppingCartOutlined, FileDoneOutlined } from "@ant-design/icons-vue";
import { userRoutes } from "@/router/user";

const router = useRouter();
const route = useRoute();
const { t, locale } = useI18n();
const { menus, currentKey, showLoginedMenu } = useMenus();
const emit = defineEmits(["clicked"]);

// 定义菜单项类型
interface MenuItem {
  key: string;
  label: string;
  path: string;
  icon: () => VNode;
  children?: MenuItem[];
}

const selectedKeys = ref<string[]>([currentKey.value]);
const openKeys = ref<string[]>([]);

// 处理菜单项，添加子菜单
const menuItems = computed<MenuItem[]>(() => {
  const items = menus.value.map(item => {
    // 为账单菜单动态添加子菜单
    if (item.key === "billing") {
      // 从路由配置中获取billing路由的子路由
      const billingRoute = userRoutes.find(route => route.name === "billing");
      if (billingRoute && billingRoute.children) {
        return {
          ...item,
          children: billingRoute.children.map(child => ({
            key: child.name as string,
            label: t(`layout.menu.${child.name}`),
            path: child.path,
            icon: () => getIconForRoute(child.name as string),
          })),
        };
      }
    }
    return item;
  });
  return items;
});

// 根据路由名称获取对应图标
function getIconForRoute(routeName: string): VNode {
  switch (routeName) {
    case "billing-overview":
      return h(FundOutlined);
    case "billing-orders":
      return h(ShoppingCartOutlined);
    case "billing-invoices":
      return h(FileDoneOutlined);
    default:
      return h(FundOutlined);
  }
}

// 监听当前路由变化，设置选中和展开的菜单项
watch(
  () => route.path,
  newPath => {
    // 查找匹配的菜单项
    for (const item of menuItems.value) {
      if (item.path && newPath === item.path) {
        selectedKeys.value = [item.key];
        break;
      }

      // 检查子菜单
      if (item.children) {
        for (const child of item.children) {
          if (child.path && newPath === child.path) {
            selectedKeys.value = [child.key];
            openKeys.value = [item.key];
            return;
          }
        }
      }
    }

    // 处理重定向情况（如/billing重定向到/billing/overview）
    if (newPath === "/billing") {
      selectedKeys.value = ["billing-overview"];
      openKeys.value = ["billing"];
    }
  },
  { immediate: true }
);

// 监听语言变化
watch(
  () => locale.value,
  () => {
    // 重新计算菜单项以更新标签
    // 触发重新渲染由computed属性自动处理
  }
);

const handleClick: MenuProps["onClick"] = e => {
  // 查找被点击的菜单项
  let targetItem: MenuItem | undefined;

  for (const item of menuItems.value) {
    if (item.key === e.key) {
      targetItem = item;
      break;
    }

    // 检查子菜单
    if (item.children) {
      const child = item.children.find(child => child.key === e.key);
      if (child) {
        targetItem = child;
        // 展开父菜单
        openKeys.value = [item.key];
        break;
      }
    }
  }

  if (targetItem && targetItem.path) {
    router.push(targetItem.path);
  }

  // 通知父组件菜单被点击，用于移动端关闭抽屉
  emit("clicked");
};
</script>
